import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MatListModule,
  type MatListOption,
  MatSelectionList,
} from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import { Patient } from '@principle-theorem/principle-core';
import {
  type IMedia,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  type WithRef,
  deletedQuery,
  Firestore,
  shareReplayCold,
  undeleteDoc,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface IRestoreDeletedMediaDialogData {
  patient: WithRef<IPatient>;
}

interface IDeletedMediaItem {
  media: WithRef<IMedia>;
  deletedAt: Date;
}

@Component({
  selector: 'pt-restore-deleted-media-dialog',
  templateUrl: './restore-deleted-media-dialog.component.html',
  styleUrls: ['./restore-deleted-media-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
  ],
})
export class RestoreDeletedMediaDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<RestoreDeletedMediaDialogComponent>>(MatDialogRef);
  private _snackBar = inject(MatSnackBar);
  data = inject<IRestoreDeletedMediaDialogData>(MAT_DIALOG_DATA);
  mediaList = viewChild<MatSelectionList>('mediaList');

  restoring = signal(false);
  selectedItems = signal<IDeletedMediaItem[]>([]);
  allSelected = signal(false);

  deletedMediaItems$: Observable<IDeletedMediaItem[]>;
  loading$: Observable<boolean>;

  constructor() {
    this.deletedMediaItems$ = Firestore.query$(
      deletedQuery(Patient.mediaCol(this.data.patient))
    ).pipe(
      map((deletedMedia) =>
        deletedMedia
          .map((media) => ({
            media,
            deletedAt: media.updatedAt.toDate(),
          }))
          .sort((a, b) => b.deletedAt.getTime() - a.deletedAt.getTime())
      ),
      shareReplayCold()
    );

    this.loading$ = this.deletedMediaItems$.pipe(
      map(() => false),
      startWith(true),
      shareReplayCold()
    );
  }

  onSelectionChange(selected: MatListOption[]): void {
    this.selectedItems.set(
      selected.map((option) => option.value as IDeletedMediaItem)
    );
    const list = this.mediaList();
    if (list) {
      this.allSelected.set(
        selected.length === list.options.length && selected.length > 0
      );
    }
  }

  toggleSelectAll(): void {
    const list = this.mediaList();
    if (!list) {
      return;
    }
    if (this.allSelected()) {
      list.deselectAll();
      this.selectedItems.set([]);
      this.allSelected.set(false);
    } else {
      list.selectAll();
      this.selectedItems.set(
        list.options.map((option) => option.value as IDeletedMediaItem)
      );
      this.allSelected.set(true);
    }
  }

  selectAll(): void {
    const list = this.mediaList();
    if (!list) {
      return;
    }
    list.selectAll();
    this.onSelectionChange(list.selectedOptions.selected);
  }

  async restoreSelected(): Promise<void> {
    const items = this.selectedItems();
    if (!items.length) {
      return;
    }

    this.restoring.set(true);
    try {
      await Promise.all(items.map((item) => undeleteDoc(item.media.ref)));
      const count = items.length;
      this._snackBar.open(
        `Restored ${count} media file${count === 1 ? '' : 's'}`,
        'OK',
        { duration: 3000 }
      );
      this._dialogRef.close(true);
    } catch {
      this._snackBar.open('Failed to restore selected media', 'OK', {
        duration: 5000,
      });
      this.restoring.set(false);
    }
  }
}
